/**
 * VentEditor - Panel for editing selected vent properties
 */

import React, { useState } from 'react';
import { FloatingPanel, PanelSection } from '../ui/Panel';
import { Button } from '../ui/Button';
import { Select, NumberInput } from '../ui/Input';
import {
  VENT_TYPES,
  SUPPLY_REGISTER_SIZES,
  RETURN_GRILLE_SIZES,
} from '../../constants/hvacEquipment';

export default function VentEditor({
  vent,
  onUpdate,
  onDelete,
  onClose,
  x = 20,
  y = 100,
  isMobile = false,
}) {
  const [minimized, setMinimized] = useState(isMobile);

  if (!vent) return null;

  const ventType = VENT_TYPES[vent.type] || VENT_TYPES['supply-floor'];
  const isSupply = ventType.category === 'supply';
  const sizes = isSupply ? SUPPLY_REGISTER_SIZES : RETURN_GRILLE_SIZES;

  const sizeOptions = sizes.map((size) => ({
    value: size.value,
    label: size.label,
  }));

  const rotationPresets = [0, 90, 180, 270];

  return (
    <FloatingPanel
      title="Vent Properties"
      onClose={onClose}
      onMinimize={() => setMinimized(!minimized)}
      minimized={minimized}
      isMobile={isMobile}
      x={x}
      y={y}
      maxHeight={450}
    >
      <div style={{ padding: '12px' }}>
        {/* Vent Type Info */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '12px',
            padding: '8px',
            background: `${ventType.color}22`,
            borderRadius: '6px',
            border: `1px solid ${ventType.color}44`,
          }}
        >
          <div
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '4px',
              background: ventType.color,
            }}
          />
          <div>
            <div style={{ fontWeight: '600', color: '#fff' }}>
              {ventType.label}
            </div>
            <div style={{ fontSize: '11px', color: '#8090a0' }}>
              {vent.size}
            </div>
          </div>
        </div>

        <PanelSection title="Type">
          <Select
            label="Vent Type"
            value={vent.type}
            onChange={(type) => {
              const newVentType = VENT_TYPES[type];
              onUpdate({ type, size: newVentType.defaultSize });
            }}
            options={[
              ...Object.entries(VENT_TYPES)
                .filter(([_, v]) => v.category === 'supply')
                .map(([key, v]) => ({ value: key, label: `Supply: ${v.label}` })),
              ...Object.entries(VENT_TYPES)
                .filter(([_, v]) => v.category === 'return')
                .map(([key, v]) => ({ value: key, label: `Return: ${v.label}` })),
            ]}
          />
        </PanelSection>

        <PanelSection title="Size">
          <Select
            label="Vent Size"
            value={vent.size}
            onChange={(size) => onUpdate({ size })}
            options={sizeOptions}
          />
        </PanelSection>

        <PanelSection title="CFM (Optional)">
          <NumberInput
            label="Airflow (CFM)"
            value={vent.cfm || 0}
            onChange={(cfm) => onUpdate({ cfm: cfm || null })}
            min={0}
            max={2000}
            suffix="CFM"
          />
        </PanelSection>

        <PanelSection title="Rotation">
          <div style={{ display: 'flex', gap: '4px' }}>
            {rotationPresets.map((deg) => (
              <Button
                key={deg}
                variant={(vent.rotation || 0) === deg ? 'primary' : 'secondary'}
                size="small"
                onClick={() => onUpdate({ rotation: deg })}
                style={{ flex: 1, fontSize: '11px' }}
              >
                {deg}°
              </Button>
            ))}
          </div>
        </PanelSection>

        <PanelSection title="Notes">
          <textarea
            value={vent.notes || ''}
            onChange={(e) => onUpdate({ notes: e.target.value })}
            placeholder="Add notes..."
            rows={2}
            style={{
              width: '100%',
              padding: '8px',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '4px',
              color: '#fff',
              fontSize: '12px',
              resize: 'vertical',
            }}
          />
        </PanelSection>

        <PanelSection title="Actions">
          <Button
            variant="danger"
            onClick={onDelete}
            style={{ width: '100%' }}
          >
            Delete Vent
          </Button>
        </PanelSection>
      </div>
    </FloatingPanel>
  );
}
